import { useState } from "react"

import { cn } from "@/lib/utils"

import { useReportPurchaseStore } from "../../hooks/report-purchase-store"
import { AstrologyReadyPopover } from "./astrology-ready-popover"
import { LoveMatchReadyPopover } from "./love-match-ready-popover"
import { SoulmateReadyPopover } from "./soulmate-ready-popover"
import { AuraScanReadyPopover } from "./aura-scan-ready-popover"
import { ReportInProgressSheet } from "./report-in-progress-sheet"
import { ReportOrbitSpinner } from "./report-orbit-spinner"

/** Slide-out + scrim fade duration before the sheet unmounts (matches CSS). */
const CLOSE_MS = 300

/**
 * Global overlay for the report-purchase flow, mounted once in the webapp shell.
 * While a charge is in flight it dims the app and centers the orbit spinner;
 * on success it slides up the "Report in Progress" sheet — the same sheet
 * re-opens when the user taps a still-unlocking row. Driven by the shared
 * report-purchase store, so any reports list (Today, Reports tab, Biorhythm)
 * triggers the same UX without duplicating it.
 */
export function ReportPurchaseOverlay() {
  const purchasingReport = useReportPurchaseStore((s) => s.purchasingReport)
  const sheetReport = useReportPurchaseStore((s) => s.sheetReport)
  const revealReport = useReportPurchaseStore((s) => s.revealReport)
  const dismissSheet = useReportPurchaseStore((s) => s.dismissSheet)
  const dismissReveal = useReportPurchaseStore((s) => s.dismissReveal)
  const [closing, setClosing] = useState(false)

  // Play the slide-down + fade-out, then unmount — so the sheet closes as
  // smoothly as it opened, whether dismissed via "Got it" or the backdrop.
  const handleDismiss = () => {
    if (closing) return
    setClosing(true)
    window.setTimeout(() => {
      setClosing(false)
      dismissSheet()
    }, CLOSE_MS)
  }

  if (purchasingReport) {
    return (
      <div className="astro-sheet-fade fixed inset-0 z-[1150] flex items-center justify-center bg-dark-base/80 backdrop-blur-sm">
        <ReportOrbitSpinner />
      </div>
    )
  }

  if (sheetReport) {
    return (
      <div className="fixed inset-0 z-[1150]">
        <button
          type="button"
          aria-label="Close"
          onClick={handleDismiss}
          className={cn(
            "absolute inset-0 h-full w-full bg-dark-base/80 backdrop-blur-sm",
            closing ? "astro-sheet-fade-out" : "astro-sheet-fade",
          )}
        />
        <div className="absolute inset-x-0 bottom-0 mx-auto w-full max-w-app">
          <ReportInProgressSheet report={sheetReport} onDismiss={handleDismiss} closing={closing} />
        </div>
      </div>
    )
  }

  if (revealReport) {
    // Astrology (star) → zodiac-sign popover; Soulmate → blurred-sketch sheet;
    // Love Match (truths) → crystal-heart.
    if (revealReport.purchaseType === "star") {
      return <AstrologyReadyPopover onClose={dismissReveal} />
    }
    if (revealReport.purchaseType === "soulmate") {
      return <SoulmateReadyPopover onClose={dismissReveal} />
    }
    if (revealReport.purchaseType === "aura") {
      return <AuraScanReadyPopover onClose={dismissReveal} />
    }
    return <LoveMatchReadyPopover onClose={dismissReveal} />
  }

  return null
}
